import { ArrowRight, KeyRound, LoaderCircle, Mail } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import {
  AuthPanel,
  FormError,
  IdentityShell,
  IdentityStatus
} from "../components/IdentityShell.tsx";
import {
  auth,
  completionFlow,
  establishCompletionSession,
  runOnce,
  safeAccountSetupReturnTo
} from "../lib/auth-client.ts";

export function AccountCompletion() {
  const query = new URLSearchParams(window.location.search);
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/u, ""));
  const flow = completionFlow(query.get("flow") ?? fragment.get("type"));
  const [returnTo] = useState(() => safeAccountSetupReturnTo(query.get("return_to")));
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!flow) {
      setError("账号设置链接无效或已过期。");
      return;
    }
    const code = query.get("code");
    const accessToken = fragment.get("access_token");
    const refreshToken = fragment.get("refresh_token");
    const tokenSession = accessToken && refreshToken ? { accessToken, refreshToken } : null;
    if (!code && !tokenSession) {
      setError("账号设置链接无效或已过期。");
      return;
    }
    const operationKey = `account-completion:${window.location.href}`;
    void runOnce(operationKey, async () => {
      const result = await establishCompletionSession(code, tokenSession);
      if (result.error || !result.data.session) {
        throw result.error ?? new Error("Supabase Auth did not establish a session");
      }
    })
      .then(() => {
        if (!active) return;
        window.history.replaceState({}, "", `/auth/complete?flow=${flow}`);
        setReady(true);
      })
      .catch(() => {
        if (active) setError("账号设置链接无效或已过期。");
      });
    return () => {
      active = false;
    };
  }, [flow]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (password !== confirmation) {
      setError("两次输入的密码不一致。");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await auth.updatePassword(password);
    if (result.error) {
      setBusy(false);
      setError("密码保存失败，请稍后再试。");
      return;
    }
    await auth.signOut();
    setBusy(false);
    setComplete(true);
  }

  if (error && !ready) {
    return (
      <IdentityStatus
        title="无法完成账号设置"
        message={error}
        variant="failed"
        step="设置"
        status="需要处理"
        action={{
          label: "重新发送邮件",
          icon: <Mail size={17} />,
          onClick: () => window.location.replace(recoveryEntryUrl(returnTo))
        }}
      />
    );
  }

  if (!ready) {
    return (
      <IdentityStatus
        title="正在验证设置链接"
        message="正在确认安全链接…"
        step="设置"
        status="验证中"
      />
    );
  }

  if (complete) {
    return (
      <IdentityStatus
        title="密码已保存"
        message="账号已就绪，请使用新密码登录。"
        variant="success"
        step="完成"
        status="密码已保存"
        action={{
          label: returnTo ? "返回 Matterline" : "前往登录",
          icon: <KeyRound size={17} />,
          onClick: () => window.location.replace(returnTo ?? "/")
        }}
      />
    );
  }

  return (
    <IdentityShell step="设置" status="需要设置密码">
      <AuthPanel
        eyebrow="账号设置"
        title={flow === "invite" ? "创建密码" : "重置密码"}
        lede={
          flow === "invite"
            ? "为你的 Matterline 账号设置登录密码。"
            : "设置新的登录密码后即可继续使用。"
        }
        footer="密码至少 12 位。设置完成后将使用新密码登录。"
      >
        <form onSubmit={submit}>
          <label htmlFor="new-password">
            新密码
            <input
              id="new-password"
              autoComplete="new-password"
              minLength={12}
              type="password"
              placeholder="至少 12 位"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              disabled={busy}
            />
            <span className="field-hint">建议使用字母、数字与符号的组合。</span>
          </label>
          <label htmlFor="confirm-password">
            确认密码
            <input
              id="confirm-password"
              autoComplete="new-password"
              minLength={12}
              type="password"
              placeholder="再次输入密码"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              required
              disabled={busy}
            />
          </label>
          {error ? <FormError>{error}</FormError> : null}
          <button className="primary" disabled={busy} type="submit">
            {busy ? <LoaderCircle className="spin" size={17} /> : <KeyRound size={17} />}
            保存密码
            <ArrowRight size={17} />
          </button>
        </form>
      </AuthPanel>
    </IdentityShell>
  );
}

function recoveryEntryUrl(returnTo: string | null): string {
  const query = new URLSearchParams({ mode: "recovery" });
  if (returnTo) query.set("return_to", returnTo);
  return `/?${query.toString()}`;
}
